/**
 * Soft!=Live Page211 — Settings Billing residual liquid (Page151/121/89/47 + dual).
 * Mount: /settings/billing
 * Buses (contract ops — never painted in operator DOM).
 * Soft residual deleted:
 *  - invent seat chips while posture loading
 *  - invent invoices / device counts when flag or seat sealed
 *  - checkout / payment CTA theatre
 *  - path /operation labels painted in DOM
 * Marker: Page211 Soft residual
 */
import { useMemo, useState } from "react";
import { html, F, Ico, KV, Card, Alert, go } from "./ui.js";
import { path, useApi } from "./api.js";
import { useSession } from "./session.js";

const FILTERS = [
  { id: "all", label: "All" },
  { id: "paid", label: "Paid" },
  { id: "unpaid", label: "Unpaid" },
];

/** Soft!=Live Page211: missing contract ops must not crash the desk. */
function safeBillingPath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__settings_billing_status") return "/settings/billing/status";
    if (op === "get__billing_status") return "/billing/status";
    if (op === "get__billing_usage") return "/billing/usage";
    if (op === "get__billing_invoices") {
      const f = opts && opts.query && opts.query.filter;
      return f
        ? "/billing/invoices?filter=" + encodeURIComponent(f)
        : "/billing/invoices";
    }
    return "";
  }
}

/** Path-free operator copy for Billing desk. */
export function softBillingSettingsErrorOf(err) {
  const n = String(err == null ? "" : err).trim();
  const low = n.toLowerCase();
  if (!low) return "That action could not be completed.";
  if (/\b403\b|forbidden|seat_gated|not_authorized|denied|insufficient|admin required/.test(low)) {
    return "Your account cannot open billing here. Ask an organisation admin if you need access.";
  }
  if (/\b401\b|unauthorized|missing bearer|session/.test(low)) {
    return "Sign in again to continue.";
  }
  if (/\b422\b|\b400\b|invalid|required|filter/.test(low)) {
    return "That billing request was not accepted. Check the filter and try again.";
  }
  if (/\b429\b|too many/.test(low)) {
    return "Too many tries - wait a moment, then retry.";
  }
  if (/\b404\b|not found|disabled|not.?armed|unavailable|\b503\b/.test(low)) {
    return "Billing is not available on this deployment yet.";
  }
  if (/network|failed to fetch|timeout/.test(low)) {
    return "Network issue - try again in a moment.";
  }
  if (/\/api\/|\/billing|\/settings\/billing|GET |POST |status_path|http/.test(low)) {
    return "That action could not be completed.";
  }
  if (n.length > 160) return n.slice(0, 157) + "...";
  return n;
}

/** Pure: seat chips from Live Settings Billing posture (Page211). */
export function billingSettingsSeatOf(post) {
  const p = post && typeof post === "object" ? post : {};
  const lim = (p.limits && typeof p.limits === "object") ? p.limits : {};
  const paint = Number(lim.invoice_paint_max);
  return {
    enabled: p.enabled === true,
    armed: p.armed === true,
    flagOn: p.enabled === true,
    usageAllowed: p.usage_plane_allowed === true,
    invoicesAllowed: p.invoices_plane_allowed === true,
    reReadAllowed: p.re_read_allowed === true,
    checkoutSurface: p.checkout_surface === true,
    role: String(p.principal_role || ""),
    detail: p.detail || null,
    postureLabel: String(p.posture_label || "").trim() || null,
    softTitle: String(p.soft_title || "").trim() || null,
    softMessage: String(p.soft_message || "").trim() || null,
    forbidApiPaint: p.soft_api_path_paint_forbidden !== false,
    invoicePaintMax:
      Number.isFinite(paint) && paint > 0 ? Math.floor(paint) : 40,
  };
}

/** Pure: invoice rows from Live list envelope. */
export function invoiceRowsOf(pack) {
  const root = pack || {};
  const rows = root.items || root.invoices || root.rows || [];
  return Array.isArray(rows) ? rows : [];
}

/**
 * Pure: flatten billing usage nested envelope.
 * Live returns plan_code + usage.devices (+ quota). Soft flat "devices" invent is forbidden.
 */
export function usageViewOf(pack) {
  const root = pack && typeof pack === "object" ? pack : {};
  const u = root.usage && typeof root.usage === "object" ? root.usage : {};
  const devices =
    u.devices != null ? u.devices
      : (root.devices != null ? root.devices : null);
  const limit =
    u.devices_limit != null ? u.devices_limit
      : (root.devices_limit != null ? root.devices_limit : null);
  const remaining =
    u.devices_remaining != null ? u.devices_remaining
      : (root.devices_remaining != null ? root.devices_remaining : null);
  return {
    owner: String(root.owner_admin || root.owner || "").trim(),
    plan: String(root.plan_code || root.plan || u.plan_code || "").trim() || "—",
    status: String(root.status || u.status || "").trim() || "—",
    paidThrough: root.paid_through_at != null ? root.paid_through_at : null,
    graceEnds: root.grace_ends_at != null ? root.grace_ends_at : null,
    devices,
    limit,
    remaining,
    enforced: !!(u.quota_enforced ?? root.quota_enforced),
    schema: String(u.schema || "").trim() || "devices_v1",
  };
}

/** Pure: amount display — never invent currency. */
export function amountTextOf(row) {
  if (!row || row.amount == null || row.amount === "") return "—";
  const cur = String(row.currency || row.currency_code || "").trim();
  return cur ? `${row.amount} ${cur}` : String(row.amount);
}

export function BillingDesk({ toast }) {
  const { me } = useSession();
  const role = String((me && me.role) || "").toLowerCase();
  const isAdmin = role === "admin" || role === "superadmin";
  const isStaff = role === "sa_staff";
  const [filter, setFilter] = useState("all");

  const posture = useApi(safeBillingPath("get__settings_billing_status"));
  const post = posture.data || {};
  // Fail-closed while loading - seats fail-closed (ignore client role invent).
  const seat = billingSettingsSeatOf(posture.loading || !posture.data ? {} : post);

  const usageOn = seat.usageAllowed;
  const invOn = seat.invoicesAllowed;
  const flagOn = seat.flagOn;

  const usage = useApi(usageOn ? safeBillingPath("get__billing_usage") : null);
  const inv = useApi(
    invOn
      ? safeBillingPath("get__billing_invoices", { query: { filter: filter || "all" } })
      : null,
  );
  const rows = useMemo(() => {
    const list = invoiceRowsOf(inv.data);
    return list.slice(0, seat.invoicePaintMax);
  }, [inv.data, seat.invoicePaintMax]);
  const vue = useMemo(() => usageViewOf(usage.data), [usage.data]);

  const softTitle = seat.softTitle || seat.postureLabel || "Billing";
  const softLede = seat.softMessage || seat.postureLabel || (
    "Plan usage and invoices for your organisation. "
    + "Counters and bills come only from the server seat - never invented here."
  );

  const reRead = () => {
    if (!seat.reReadAllowed) {
      toast && toast("You cannot re-read this desk from this account", "warn");
      return;
    }
    posture.reload && posture.reload();
    usage.reload && usage.reload();
    inv.reload && inv.reload();
    toast && toast("Billing refreshed", "go");
  };

  const rail = (title, note) => html`
    <div className="bill-desk__rail">
      <div>
        <p className="eyebrow">Settings · Billing</p>
        <h2 className="bill-desk__title">${title}</h2>
        <p className="note">${note}</p>
      </div>
      <div className="btn-row bill-desk__acts">
        <button type="button" className="btn btn--ghost btn--sm" onClick=${reRead}
          disabled=${posture.loading || !seat.reReadAllowed}>
          <${Ico} d="refresh" w=${13}/> Refresh
        </button>
        <button type="button" className="btn btn--ghost btn--sm"
          onClick=${() => go("/settings/delegations")}>Staff delegations</button>
      </div>
    </div>`;

  const dualStrip = () => html`
    <${Alert} tone="info"
      title=${flagOn
        ? (usageOn || invOn ? "Billing seat follows the server" : "Billing sealed for this seat")
        : "Billing not armed on this deployment"}
      say=${`Signed in as ${seat.role || role || "operator"}. `
        + `Usage ${seat.usageAllowed ? "open" : "sealed"} · invoices ${seat.invoicesAllowed ? "open" : "sealed"} · `
        + `flag ${flagOn ? "on" : "off"}. No checkout surface on this console.`}/>`;

  // Seat floors - balances sealed when both buses closed.
  if (!isAdmin && !posture.loading && !seat.usageAllowed && !seat.invoicesAllowed) {
    return html`<div className="bill-desk bill-desk--liquid"
      data-cmc-page="page121-billing" data-cmc-bill="page47" data-cmc-legacy="page89-billing"
      data-cmc-residual="page211-billing" data-cmc-bill151="page151-billing"
      data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
      ${rail(softTitle, softLede)}
      ${dualStrip()}
      <${Alert} tone="info"
        title=${isStaff ? "Platform staff — tenant billing stays sealed" : "Organisation admin required"}
        say=${softLede}/>
      <${Card} title="Billing posture" meta="read-only honesty">
        ${posture.loading && !posture.data ? html`<p className="note">Reading posture…</p>`
          : posture.error ? html`<${Alert} tone="warn" title="Could not read billing posture"
              say=${softBillingSettingsErrorOf(posture.error) || "Status refused for this account."}/>`
          : html`<div className="bill-desk__kpis">
              <div className="bill-desk__kpi" data-on=${flagOn ? "1" : "0"}>
                <span>Deployment</span><b>${flagOn ? "on" : "off"}</b><em>flag</em></div>
              <div className="bill-desk__kpi" data-on="0">
                <span>Usage</span><b>sealed</b><em>independent</em></div>
              <div className="bill-desk__kpi" data-on="0">
                <span>Invoices</span><b>sealed</b><em>independent</em></div>
              <div className="bill-desk__kpi" data-on="0">
                <span>Checkout</span><b>absent</b><em>no self-serve</em></div>
            </div>`}
      <//>
    </div>`;
  }

  if (!flagOn && !usageOn && !invOn) {
    return html`<div className="bill-desk bill-desk--liquid"
      data-cmc-page="page121-billing" data-cmc-bill="page47" data-cmc-legacy="page89-billing"
      data-cmc-residual="page211-billing" data-cmc-bill151="page151-billing"
      data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
      ${rail(softTitle, softLede)}
      ${dualStrip()}
      <${Alert} tone="warn" title="Billing not armed"
        say=${softLede}/>
      <${Card} title="Billing posture" meta="deployment honesty"
          sub="Usage and invoices stay closed until billing is armed. Nothing is invented while off.">
        ${posture.error ? html`<${Alert} tone="warn" title="Could not read billing posture"
          say=${softBillingSettingsErrorOf(posture.error) || "Status refused."}/>` : null}
        <div className="bill-desk__kpis">
          <div className="bill-desk__kpi" data-on="0"><span>Deployment</span><b>${posture.loading ? "…" : "off"}</b><em>flag</em></div>
          <div className="bill-desk__kpi" data-on="0"><span>Usage</span><b>sealed</b><em>independent</em></div>
          <div className="bill-desk__kpi" data-on="0"><span>Invoices</span><b>sealed</b><em>independent</em></div>
          <div className="bill-desk__kpi" data-on="0"><span>Checkout</span><b>absent</b><em>no self-serve</em></div>
        </div>
        <p className="note">Self-serve checkout is not on this console. When armed, filters and refresh hit the server - empty lists stay empty.</p>
      <//>
    </div>`;
  }

  const usageBad = !!(usage.error && !usage.loading);
  const invBad = !!(inv.error && !inv.loading);

  return html`<div className="bill-desk bill-desk--liquid"
    data-cmc-page="page121-billing" data-cmc-bill="page47" data-cmc-legacy="page89-billing"
    data-cmc-residual="page211-billing" data-cmc-bill151="page151-billing"
    data-soft-api-path-paint-forbidden=${seat.forbidApiPaint ? "1" : "0"}>
    ${rail(softTitle, softLede)}
    ${dualStrip()}

    <${Alert} tone="info" title="Server billing only"
      say="Device counts come from usage.devices on the server. Empty invoices are empty - never filled in the browser."/>

    <div className="bill-desk__kpis">
      <div className="bill-desk__kpi" data-on=${usageOn && !usageBad ? "1" : "0"}>
        <span>Plan</span><b>${!usageOn ? "sealed" : (usage.loading ? "…" : vue.plan)}</b><em>usage</em>
      </div>
      <div className="bill-desk__kpi" data-on=${usageOn && !usageBad ? "1" : "0"}>
        <span>Status</span><b>${!usageOn ? "sealed" : (usage.loading ? "…" : vue.status)}</b><em>account</em>
      </div>
      <div className="bill-desk__kpi" data-on=${usageOn && !usageBad ? "1" : "0"}>
        <span>Devices</span>
        <b>${!usageOn ? "sealed" : (usage.loading ? "…" : (vue.devices != null ? String(vue.devices) : "—"))}</b>
        <em>quota</em>
      </div>
      <div className="bill-desk__kpi" data-on=${invOn && !invBad ? "1" : "0"}>
        <span>Invoices</span>
        <b>${!invOn ? "sealed" : (inv.loading ? "…" : String(rows.length))}</b>
        <em>list</em>
      </div>
    </div>

    <div className="bill-desk__grid">
      ${!usageOn ? html`
        <${Alert} tone="info" title="Usage sealed"
          say="This account cannot load plan or device quota from the server."/>`
      : html`
      <${Card} title="Usage" meta="plan · devices · quota"
          sub="Nested server envelope - devices under usage only. Nothing is flattened inventively.">
        ${usage.loading && !usage.data
          ? html`<p className="note">Reading usage…</p>`
          : usageBad
            ? html`<${Alert} tone="warn" title="Could not load usage"
                say=${softBillingSettingsErrorOf(usage.error) || "Usage refused for this account."}/>`
            : html`<${F}>
                <${KV} k="Owner" v=${vue.owner || "—"}/>
                <${KV} k="Plan" v=${vue.plan}/>
                <${KV} k="Status" v=${vue.status}/>
                <${KV} k="Devices" v=${vue.devices != null ? String(vue.devices) : "—"}/>
                <${KV} k="Limit" v=${vue.limit != null ? String(vue.limit) : "—"}/>
                <${KV} k="Quota left" v=${vue.enforced
                  ? (vue.remaining != null ? String(vue.remaining) : "—")
                  : "∞"}/>
                <${KV} k="Paid through" v=${vue.paidThrough != null ? String(vue.paidThrough) : "—"}/>
                <${KV} k="Schema" v=${vue.schema}/>
              <//>`}
      <//>`}

      ${!invOn ? html`
        <${Alert} tone="info" title="Invoices sealed"
          say="This account cannot list invoice rows from the server."/>`
      : html`
      <${Card} title="Invoices" meta=${"filter · " + filter}
          sub="Filter is applied by the server. Empty means empty - never seed rows.">
        <div className="chips" style=${{ marginBottom: 12 }}>
          ${FILTERS.map((f) => html`
            <button key=${f.id} type="button" className="chip" data-tone="ai"
              data-sel=${filter === f.id ? "1" : "0"}
              onClick=${() => setFilter(f.id)}>${f.label}</button>`)}
        </div>
        ${inv.loading && !inv.data
          ? html`<p className="note">Reading invoices…</p>`
          : invBad
            ? html`<${Alert} tone="warn" title="Could not load invoices"
                say=${softBillingSettingsErrorOf(inv.error) || "Invoices refused for this account."}/>`
            : !rows.length
              ? html`<p className="note">No invoices for this filter.</p>`
              : html`<div className="rows">
                  ${rows.map((row, i) => html`
                    <div key=${row.id || row.provider_bill_id || i} className="row row--say">
                      <${Ico} d="book" w=${14}/>
                      <span className="main">
                        <b>${row.status || row.state || "invoice"}</b>
                        <span>${amountTextOf(row)} · ${row.provider_bill_id || row.id || "—"}</span>
                      </span>
                    </div>`)}
                </div>`}
      <//>`}
    </div>
  </div>`;
}

export default BillingDesk;
